import { useEffect, useRef, useState } from 'react';

import { monolithApi } from '@/utils/api';
import loadChargebeeScript from '@/utils/loadChargebeeScript';
import { addCreditCardPath } from '@/utils/monolithApiRouteService';

type AddPaymentSourceCallbacks = {
  onReady?: () => void;
  onFormLoaded?: () => void;
  onCheckoutClosed?: () => void;
};

const useAddPaymentSource = (organizationSlug: string, onSuccess: () => void) => {
  const [error, setError] = useState<string>();
  const messageListenerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const removeMessageListener = () => {
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
      messageListenerRef.current = null;
    }
  };

  const addPaymentSource = async ({ onReady, onFormLoaded, onCheckoutClosed }: AddPaymentSourceCallbacks = {}) => {
    try {
      await loadChargebeeScript();
    } catch {
      setError('Failed to load payment provider. Please refresh and try again.');
      onCheckoutClosed?.();
      return;
    }

    removeMessageListener();

    const chargebeeInstance = Chargebee.getInstance();

    // When the hosted page is created with iframe_messaging: true, Chargebee posts a message
    // to the parent window instead of triggering the openCheckout success callback.
    const handleMessage = (event: MessageEvent) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          // not JSON
        }
      }
      if (data?.key === 'cb.payment_source.add' && data?.status === 'success') {
        removeMessageListener();
        chargebeeInstance.closeAll();

        onSuccess();
      }
    };
    messageListenerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);

    chargebeeInstance.openCheckout({
      async hostedPage() {
        const resp = await monolithApi.post(addCreditCardPath(organizationSlug), {}, { transformResponse: undefined });
        return resp.data;
      },
      // openCheckout returns before the hosted page request resolves; this fires only once the form is actually shown.
      loaded() {
        onFormLoaded?.();
      },
      error() {
        removeMessageListener();
        setError('Something went wrong while verifying your credit card. Please try again.');
        onCheckoutClosed?.();
      },
      close() {
        onCheckoutClosed?.();
      },
    });

    onReady?.();
  };

  useEffect(() => {
    return () => {
      removeMessageListener();
    };
  }, []);

  return { addPaymentSource, error };
};

export default useAddPaymentSource;
